import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import './Toast.css'

/**
 * Sitewide toast/notification bar. Contract mirrors Prod's admin-only
 * ToastProvider/useToast (src/pages/admin/ui.jsx) exactly — useToast()
 * returns a push function called as toast(message, type) with
 * type: 'success' | 'error' | 'info' — so this can drop in wherever Prod's
 * admin panel already calls useToast(), while also running app-wide here
 * (Prod only mounts it inside AdminPage).
 *
 * On top of Prod's contract, this also self-wires window 'error' and
 * 'unhandledrejection' listeners so any uncaught exception or rejected
 * promise anywhere on the site surfaces as an error toast automatically,
 * without every call site needing its own try/catch + toast() call.
 */
const ToastCtx = createContext(null)

const ICONS = {
  success: '✓',
  error: '!',
  info: 'i',
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (msg, type = 'success', duration = 6000) => {
      const id = Date.now() + Math.random()
      setToasts((prev) => [...prev, { id, msg, type, duration }])
      setTimeout(() => dismiss(id), duration)
    },
    [dismiss],
  )

  useEffect(() => {
    function onError(event) {
      console.error('Uncaught error:', event.error ?? event.message)
      toast(event.error?.message || 'Something went wrong. Please try again.', 'error')
    }
    function onRejection(event) {
      console.error('Unhandled promise rejection:', event.reason)
      const reason = event.reason
      const msg = typeof reason === 'string' ? reason : reason?.message
      toast(msg || 'Something went wrong. Please try again.', 'error')
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [toast])

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="toast-bar-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast-bar toast-bar--${t.type}`} role={t.type === 'error' ? 'alert' : 'status'}>
            <span className="toast-bar__icon" aria-hidden="true">{ICONS[t.type] ?? ICONS.info}</span>
            <span className="toast-bar__msg">{t.msg}</span>
            <button type="button" className="toast-bar__close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
              &times;
            </button>
            <span className="toast-bar__timer" style={{ animationDuration: `${t.duration}ms` }} />
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export function useToast() {
  return useContext(ToastCtx)
}
